import { useCallback } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { Api } from 'src/models/Api.model';
import { I18NextNS } from 'src/models/Language.model';

import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';

import { emailPattern } from 'src/utils/validationRules';

import LoginContainer from './LoginContainer';

const ForgotPassword = () => {
  const history = useHistory();

  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.PAGES);

  const { handleHttpRequestError } = useHandleHttpRequestError();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      email: '',
    },
  });

  const handleSendResetCode = useCallback(
    async (email: string) => {
      try {
        await Api.remote.sendResetCode(email);
        history.push(`/reset-password-with-otp/${email}`);
      } catch (err) {
        const errorMessage = handleHttpRequestError(err);
        Api.alertBox({
          title: t('bootbox.title.Error', {
            ns: I18NextNS.COMMON,
          }),
          message: errorMessage,
          locale: language,
          type: 'error',
        });
      }
    },
    [handleHttpRequestError, history, language, t],
  );

  const onSubmit: SubmitHandler<{
    email: string;
  }> = (data) => {
    handleSendResetCode(data.email);
  };

  return (
    <LoginContainer>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>
              <strong>{t('loginLogout.fields.registeredEmail')}</strong>
            </Form.Label>
            <Form.Control
              autoFocus
              id="email"
              type="text"
              placeholder={t('loginLogout.fields.emailPlaceholder')}
              autoComplete="off"
              {...register('email', {
                required: t('loginLogout.fields.emailRequired'),
                pattern: {
                  value: emailPattern,
                  message: t('loginLogout.fields.emailInvalid'),
                },
                maxLength: {
                  value: 254,
                  message: t('loginLogout.fields.emailMaxLength'),
                },
              })}
            ></Form.Control>

            {errors.email?.message && (
              <p className="text-danger">{errors.email?.message}</p>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary-100"
            onClick={() => {
              history.goBack();
            }}
          >
            {t('changePassword.back')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={isSubmitting}
            data-test="connect-camera"
          >
            {isSubmitting
              ? t('changePassword.submitting')
              : t('changePassword.continue')}
          </Button>
        </Modal.Footer>
      </Form>
    </LoginContainer>
  );
};

export default ForgotPassword;
